import React, { useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { PaneMenu, Button, MultiColumnList, Icon, RadioButton, RadioButtonGroup, Accordion, AccordionSet, Tooltip, Layout, Headline, Loading, } from "@folio/stripes/components";
import SearchLayout from "../components/SearchLayout";
import OrganizationDetailPane from "../components/OrganizationDetailPane";
import StatusBadge from "../components/StatusBadge";
import useOrganizationList from "../hooks/useOrganizationList";
import useNavigation from "../hooks/useNavigation";
import { usePermissions } from "../utils/permissionsUtils";
var OrganizationsList = function (_a) {
    var match = _a.match, selectedId = _a.selectedId;
    var intl = useIntl();
    var _b = useNavigation(match.path), goToList = _b.goToList, goToDetail = _b.goToDetail, goToCreate = _b.goToCreate, goToEdit = _b.goToEdit;
    var canCreateOrganization = usePermissions().canCreateOrganization;
    var list = useOrganizationList();
    /*
     * The query failed and we have nothing to show. Gated on `items.length` on
     * purpose: react-query runs with `keepPreviousData`, so a failed *refetch*
     * can still hold the previous page -- showing that stale list (with its real
     * count) beats replacing it with an error screen. One flag drives both the
     * body and the pane sub-heading so the two cannot drift apart and produce
     * "error below, 0 records above".
     */
    var showLoadError = list.isError && list.items.length === 0;
    var handleRowClick = useCallback(function (_e, row) {
        goToDetail(row.id);
    }, [goToDetail]);
    var resultsFormatter = {
        name: function (org) {
            var MAX_NAME_LENGTH = 50;
            if (org.name.length <= MAX_NAME_LENGTH) {
                return org.name;
            }
            var truncated = "".concat(org.name.slice(0, MAX_NAME_LENGTH), "...");
            return (React.createElement(Tooltip, { text: org.name, id: "name-tooltip-".concat(org.id) }, function (_a) {
                var ref = _a.ref, ariaIds = _a.ariaIds;
                return (React.createElement("span", { ref: ref, "aria-labelledby": ariaIds.text }, truncated));
            }));
        },
        sigla: function (org) { return org.sigla || ""; },
        status: function (org) { return React.createElement(StatusBadge, { status: org.status }); },
        userCount: function (org) { return org.userCount || 0; },
    };
    var filterContent = (React.createElement(AccordionSet, null,
        React.createElement(Accordion, { label: intl.formatMessage({
                id: "ui-czna-users.organizations.filter.status",
            }), separator: false },
            React.createElement(RadioButtonGroup, { value: list.statusFilter, onChange: function (e) {
                    return list.handleStatusFilterChange(e.target.value);
                } },
                React.createElement(RadioButton, { label: intl.formatMessage({
                        id: "ui-czna-users.organizations.filter.status.all",
                    }), value: "ALL" }),
                React.createElement(RadioButton, { label: intl.formatMessage({
                        id: "ui-czna-users.organizations.filter.status.active",
                    }), value: "ACTIVE" }),
                React.createElement(RadioButton, { label: intl.formatMessage({
                        id: "ui-czna-users.organizations.filter.status.inactive",
                    }), value: "INACTIVE" })))));
    var detailPane = selectedId ? (React.createElement(OrganizationDetailPane, { id: selectedId, onClose: goToList, onEdit: goToEdit })) : undefined;
    return (React.createElement(SearchLayout, { list: list, searchPlaceholder: intl.formatMessage({
            id: "ui-czna-users.organizations.search.placeholder",
        }), paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-users.organizations.title" }), paneSub: showLoadError
            ? intl.formatMessage({
                id: "ui-czna-users.organizations.loadError.countUnknown",
            })
            : undefined, filterContent: filterContent, detailPane: detailPane, lastMenu: canCreateOrganization ? (React.createElement(PaneMenu, null,
            React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, onClick: goToCreate },
                React.createElement(Icon, { icon: "plus-sign" },
                    React.createElement(FormattedMessage, { id: "ui-czna-users.organizations.new" }))))) : undefined }, list.isLoading && list.items.length === 0 ? (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
        React.createElement(Layout, { className: "padding-all-gutter" },
            React.createElement(Loading, { size: "xlarge" })))) : showLoadError ? (
    /*
     * A failed request must never fall through to the empty state below.
     * The hook coalesces a failure to `items: []` / `totalRecords: 0`, so
     * without this branch the screen reads "Organizace / 0 nalezenych
     * zaznamu" -- pixel-identical to a genuinely empty database. The
     * cataloguer cannot tell "there are none" from "we failed to find
     * out", and neither could three e2e runs, which timed out against a
     * snapshot of a plausible-looking empty list (CZNA-696).
     */
    React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
        React.createElement(Layout, { className: "padding-all-gutter" },
            React.createElement(Headline, { size: "medium", margin: "none" },
                React.createElement(FormattedMessage, { id: "ui-czna-users.organizations.loadError.heading" })),
            React.createElement("p", { style: { color: "#666", marginTop: "8px" } },
                React.createElement(FormattedMessage, { id: "ui-czna-users.organizations.loadError.message" })),
            React.createElement(Button, { buttonStyle: "primary", onClick: function () { return list.refetch(); }, style: { marginTop: "16px" } },
                React.createElement(FormattedMessage, { id: "ui-czna-users.organizations.loadError.retry" }))))) : list.items.length === 0 && !list.isLoading ? (list.isDefaultFilters ? (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
        React.createElement(Layout, { className: "padding-all-gutter" },
            React.createElement(Headline, { size: "medium", margin: "none" },
                React.createElement(FormattedMessage, { id: "ui-czna-users.organizations.empty.heading" })),
            React.createElement("p", { style: { color: "#666", marginTop: "8px" } },
                React.createElement(FormattedMessage, { id: "ui-czna-users.organizations.empty.subtext" })),
            canCreateOrganization && (React.createElement(Button, { buttonStyle: "primary", onClick: goToCreate, style: { marginTop: "16px" } },
                React.createElement(FormattedMessage, { id: "ui-czna-users.organizations.new" })))))) : (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
        React.createElement(Layout, { className: "padding-all-gutter" },
            React.createElement("p", { style: { color: "#666" } },
                React.createElement(FormattedMessage, { id: "ui-czna-users.organizations.noResults" })),
            React.createElement(Button, { onClick: list.handleClearFilters, style: { marginTop: "16px" } },
                React.createElement(FormattedMessage, { id: "ui-czna-users.organizations.clearFilters" })))))) : (
    /* @ts-expect-error - stripes MCL types require all entity keys but only visible columns are needed */
    React.createElement(MultiColumnList, { autosize: true, virtualize: true, loading: list.isFetching, contentData: list.items, totalCount: list.totalRecords, pagingType: "prev-next", pagingOffset: list.pageOffset, pageAmount: list.pageSize, columnMapping: {
            name: React.createElement(FormattedMessage, { id: "ui-czna-users.organizations.name" }),
            sigla: React.createElement(FormattedMessage, { id: "ui-czna-users.organizations.sigla" }),
            status: (React.createElement(FormattedMessage, { id: "ui-czna-users.organizations.status" })),
            userCount: (React.createElement(FormattedMessage, { id: "ui-czna-users.organizations.userCount" })),
        }, columnWidths: {
            name: "45%",
            sigla: "15%",
            status: "20%",
            userCount: "20%",
        }, visibleColumns: ["name", "sigla", "status", "userCount"], formatter: resultsFormatter, sortedColumn: list.sortColumn, sortDirection: list.sortDirection, onHeaderClick: list.handleSort, nonInteractiveHeaders: ["sigla", "userCount"], selectedRow: selectedId ? { id: selectedId } : undefined, onRowClick: handleRowClick, onNeedMoreData: function (_amount, offset) {
            return list.fetchMoreData(offset);
        } }))));
};
export default OrganizationsList;
